import { ClerkProvider, useAuth } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { RouterProvider } from "@tanstack/react-router";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { AnalyticsIdentity, initAnalytics } from "./lib/analytics";
import { router } from "./router";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
Sentry.init({
  dsn: sentryDsn,
  enabled: Boolean(sentryDsn),
  environment: import.meta.env.MODE,
  release: `clarent-web@${__APP_VERSION__}`,
});
initAnalytics();

const convexUrl = import.meta.env.VITE_CONVEX_URL;
const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function MissingConfig({ names }: { names: string[] }) {
  return (
    <main className="mx-auto max-w-lg p-8">
      <h1 className="page-title">Backplan is not configured</h1>
      <p className="mt-2 text-sm text-muted">Set these variables in <code>apps/web/.env.local</code> and restart the dev server:</p>
      <ul className="mt-3 list-disc pl-5 font-mono text-sm">
        {names.map((n) => (
          <li key={n}>{n}</li>
        ))}
      </ul>
    </main>
  );
}

const missing = [!convexUrl && "VITE_CONVEX_URL", !clerkKey && "VITE_CLERK_PUBLISHABLE_KEY"].filter((x): x is string => Boolean(x));

function App() {
  if (missing.length > 0) return <MissingConfig names={missing} />;
  const convex = new ConvexReactClient(convexUrl);
  return (
    <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
      <AnalyticsIdentity />
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        <RouterProvider router={router} />
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <main className="p-8 text-sm">
          <h1 className="page-title">Something went wrong</h1>
          <p className="mt-2 text-muted">The error has been logged. Reload the page to continue.</p>
        </main>
      }
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
